import './coapplicantUnemploymentDetailsPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentIncomeGrossAnnual,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeGrossAnnual,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      grossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantCurrentIncomeGrossAnnual,
      ),
    },
    delayError: 200,
    mode: 'onBlur',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { grossAnnualIncomeValue } = values;

    if (!grossAnnualIncomeValue) {
      return;
    }

    setCoapplicantCurrentIncomeGrossAnnual(grossAnnualIncomeValue);

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-unemployment-details-page"
        sx={theme.pageLayout}
      >
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantUnemploymentDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantUnemploymentDetails.form.h1
                }
              >
                {t('CoapplicantUnemploymentDetailsPage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantUnemploymentDetails.form.h2
                }
              >
                {t('CoapplicantUnemploymentDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantUnemploymentDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register(
                      'grossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    error={!isEmpty(errors.grossAnnualIncomeValue)}
                    label={t(
                      'CoapplicantUnemploymentDetailsPage.grossAnnualIncome',
                    )}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantUnemploymentDetails.form
                            .grossAnnualIncomeInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'grossAnnualIncomeValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.grossAnnualIncomeValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantUnemploymentDetails.form
                          .grossAnnualIncomeInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.grossAnnualIncomeValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantUnemploymentDetails.form
                      .submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantUnemploymentDetailsPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantUnemploymentDetails.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantUnemploymentDetailsPage';
